import {
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react";
import { estimateMaxClaimsPerTx } from "../../helpers/batchClaim";
import { WithdrawalTicket } from "../../helpers/dataStructures";
import { formatSmartFixed6 } from "../../utils/format";

const CARD_BG = "#1a2433";

interface Props {
  currency: string;
  currentEpoch: number;
  tickets: WithdrawalTicket[];
  onClaimClick: (ticket: WithdrawalTicket) => void;
  onClaimAll?: () => void;
  claimingTicketId: string | null;
  isBatchClaiming?: boolean;
  image: string;
}

export const WithdrawalTable = ({
  tickets,
  currentEpoch,
  onClaimClick,
  onClaimAll,
  claimingTicketId,
  isBatchClaiming = false,
  image,
  currency,
}: Props) => {
  const requestLabel = tickets.length === 1 ? "1 request" : `${tickets.length} requests`;
  const readyTickets = tickets.filter((t) => currentEpoch >= t.availableEpoch);
  const readyCount = readyTickets.length;
  const maxPerTx = estimateMaxClaimsPerTx();
  const claimAllDisabled = isBatchClaiming || claimingTicketId != null;

  const claimAllLabel =
    readyCount > maxPerTx
      ? `Claim all (${readyCount}) · ${Math.ceil(readyCount / maxPerTx)} txs`
      : `Claim all (${readyCount})`;

  return (
    <VStack spacing={4} align="stretch" width="100%">
      <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
        <Text fontSize="md" fontWeight="semibold" color="white">
          Unstaking requests
        </Text>
        <HStack spacing={3}>
          {readyCount >= 2 && onClaimAll && (
            <Button
              className="claim-box-button claim-box-button--inline"
              size="sm"
              onClick={onClaimAll}
              isLoading={isBatchClaiming}
              isDisabled={claimAllDisabled}
            >
              {claimAllLabel}
            </Button>
          )}
          <Text fontSize="sm" color="whiteAlpha.700" fontWeight="medium">
            {requestLabel}
          </Text>
        </HStack>
      </Flex>

      <VStack spacing={3} align="stretch">
        {tickets.map((ticket) => {
          const isReady = currentEpoch >= ticket.availableEpoch;
          const ticketId = ticket.base.toString();

          return (
            <Box
              key={ticketId}
              bg={CARD_BG}
              borderRadius="2xl"
              border="1px solid"
              borderColor={isReady ? "rgba(95, 244, 244, 0.35)" : "whiteAlpha.200"}
              p={4}
              transition="border-color 0.2s ease"
            >
              <Flex
                direction={{ base: "column", md: "row" }}
                align={{ base: "stretch", md: "center" }}
                justify="space-between"
                gap={4}
                width="100%"
              >
                <HStack spacing={3} align="center" flex={1} minW="0" width="100%">
                  <Image
                    src={image}
                    alt={currency}
                    boxSize="36px"
                    borderRadius="full"
                    flexShrink={0}
                  />
                  <Box flex={1} minW="0">
                    <Text
                      fontSize="xl"
                      fontWeight="semibold"
                      color="white"
                      lineHeight="1.2"
                      whiteSpace="normal"
                    >
                      {formatSmartFixed6(ticket.vrtAmount)}{" "}
                      <Text as="span" fontSize="sm" color="whiteAlpha.700" fontWeight="medium">
                        {currency}
                      </Text>
                    </Text>
                    <Text fontSize="xs" color="whiteAlpha.600" mt={0.5} whiteSpace="normal">
                      {isReady
                        ? "Ready to claim"
                        : `Claim in ${ticket.estimatedTimeToClaim}`}
                    </Text>
                  </Box>
                </HStack>

                <Button
                  className="claim-box-button claim-box-button--inline"
                  isDisabled={!isReady || claimAllDisabled}
                  onClick={() => onClaimClick(ticket)}
                  isLoading={claimingTicketId === ticketId}
                  size="sm"
                  flexShrink={0}
                  alignSelf={{ base: "stretch", md: "center" }}
                >
                  Claim
                </Button>
              </Flex>
            </Box>
          );
        })}
      </VStack>
    </VStack>
  );
};
